<script setup lang="ts">
import Filter from '@/components/filters/Filter.vue';
import { OrganizationRole } from '@/enums/roles';
import { useAuthStore } from '@/stores/auth';
import { useModuleStore } from '@/stores/module';
import { useOrganizationStore } from '@/stores/organization';
import { actionPointStatusTypeItems, actionPointSubjects } from '@/types/actionPoint';
import type { Employee } from '@/types/employee';
import type { FilterGroup } from '@/types/filter';
import type { Module } from '@/types/module';
import type { NameIdPair } from '@/types/nameIdPair';
import type { Organization } from '@/types/organization';
import type { Priority } from '@/types/priority';
import type { Risk } from '@/types/risk';
import type { Source } from '@/types/source';
import type { Team } from '@/types/team';
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

const moduleStore = useModuleStore();
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();

const emit = defineEmits(['applyFilters']);

// TODO: INC-376 refactor this so these get only fetched when the filter menu is opened. Currently reused from ActionPoints view which uses these too for the dialogs
const props = defineProps<{
    teams: Team[];
    employees: Employee[];
    risks: Risk[];
    priorities: Priority[];
    sources: Source[];
    organization: Organization;
    isInitializing: boolean;
}>();

const now = new Date();
const dateOptions = [
    { title: 'Vandaag', value: `gte:${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}` },
    { title: 'Deze maand', value: `gte:${now.getFullYear()}-${now.getMonth() + 1}-01` },
    { title: 'Dit jaar', value: `gte:${now.getFullYear()}-01-01` }
];

const filterGroups = ref<FilterGroup[]>([]);
const organizations = ref<NameIdPair[]>([]);

const { isActionPointExecutorFeatureEnabled } = storeToRefs(authStore);

function upsertFilterGroups(groups: FilterGroup[]) {
    groups.forEach((group) => {
        const existing = filterGroups.value.find((fg) => fg.property === group.property);

        if (existing) {
            existing.options = group.options;
        } else {
            filterGroups.value.push(group);
        }
    });
}

async function upsertFilterOptions() {
    if (props.isInitializing) return;

    const possibleResponsiblesAndOrExecutors = [
        ...props.employees.map((e) => ({ title: e.fullName, value: e._id, icon: 'person' })),
        ...props.teams.map((t) => ({ title: t.name, value: t._id, icon: 'group' }))
    ];

    filterGroups.value = [
        { title: 'Risiconiveau', property: 'risk', options: props.risks.map((r: Risk) => ({ title: r.description, value: r._id })) },
        { title: 'Prioriteit', property: 'priority', options: props.priorities.map((p: Priority) => ({ title: p.name, value: p._id })) },

        {
            title: 'Verantwoordelijke',
            property: 'responsible.entityId',
            options: possibleResponsiblesAndOrExecutors
        }
    ];

    if (isActionPointExecutorFeatureEnabled.value) {
        filterGroups.value.push({
            title: 'Uitvoerder',
            property: 'executor.entityId',
            options: possibleResponsiblesAndOrExecutors
        });
    }

    filterGroups.value.push(
        { title: 'Bron', property: 'source', options: props.sources.map((s) => ({ title: s.name, value: s._id })) },
        {
            title: 'Onderwerp',
            property: 'subject',
            options: actionPointSubjects.map((subject, i) => ({ title: `${i + 1}. ${subject}`, value: i.toString() }))
        },
        {
            title: 'Status',
            property: 'status',
            options: [...actionPointStatusTypeItems.map((i) => ({ title: i.title, value: i.value })), { title: 'Niet afgerond', value: 'ne:done' }]
        }
    );

    if (moduleStore.areMultipleModulesAccessible) {
        filterGroups.value.push({
            title: 'Module',
            property: 'module',
            options: moduleStore.modules.map((m: Module) => ({ title: m.name, value: m._id }))
        });
    }

    filterGroups.value.push(
        { title: 'Afgerond', property: 'completedOn', options: dateOptions },
        { title: 'Aangemaakt', property: 'createdOn', options: dateOptions }
    );

    upsertFilterGroups(filterGroups.value);
}

async function onFilterMenuOpen() {
    if (!authStore.isAuthorized([OrganizationRole.Admin])) return;

    const currentAndChildrenOrgNameIdPairs = await organizationStore.getChildrenNameIdPairs(props.organization._id);

    const orgs = [{ _id: props.organization._id, name: props.organization.name }];

    if (currentAndChildrenOrgNameIdPairs?.children?.length) orgs.push(...currentAndChildrenOrgNameIdPairs.children);

    organizations.value = orgs;

    if (!authStore.isAuthorized([OrganizationRole.Admin])) return;

    const filterGroup = filterGroups.value.find((fg) => fg.property == 'organization');
    if (filterGroup && filterGroup.options.length > 1) return;

    if (organizations.value.length > 1) {
        filterGroups.value.push({
            title: 'Bedrijf',
            property: 'organization',
            options: [...orgs].map((org) => ({ title: org.name, value: org._id }))
        });
    }
}

watch(
    () => props.isInitializing,
    () => {
        if (props.isInitializing) return;
        upsertFilterOptions();
    }
);
</script>

<template>
    <Filter :filterGroups="filterGroups" @apply-filters="emit('applyFilters')" @filter-menu-opened="onFilterMenuOpen" />
</template>
